/**
 * Promo Code Redemption Page
 * 
 * WEB-ONLY page for TestFlight/internal testers to redeem promo codes.
 * This page is NOT accessible from within the iOS/Android native apps.
 * 
 * Apple Guideline 3.1.1: No custom promo code redemption in iOS apps.
 * This page only works on web browser access.
 * 
 * URL: /redeem (web only)
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Ship, Gift, Crown, Check, AlertCircle, Ban } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const PromoRedeemPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isNativeApp, setIsNativeApp] = useState(false);

  // Check if running in native app - block access
  useEffect(() => {
    const native = window.Capacitor?.isNativePlatform?.() || false;
    setIsNativeApp(native);
  }, []);

  // Block native app access - Apple Guideline 3.1.1
  if (isNativeApp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Ban className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Not Available</h2>
            <p className="text-gray-600 mb-4">
              Promo codes cannot be redeemed in the app. Please subscribe through the App Store or Google Play.
            </p>
            <Button onClick={() => window.history.back()} className="w-full">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleRedeem = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Step 1: Login to get token
      const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!loginRes.ok) {
        const data = await loginRes.json();
        throw new Error(data.detail || 'Invalid email or password');
      }

      const { access_token } = await loginRes.json();

      // Step 2: Redeem promo code
      const redeemRes = await fetch(`${API_BASE_URL}/api/subscription/redeem-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${access_token}`
        },
        body: JSON.stringify({ code: promoCode.trim().toUpperCase() })
      });

      const redeemData = await redeemRes.json();

      if (!redeemRes.ok) {
        throw new Error(redeemData.detail || 'Failed to redeem code');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to Boat 360 Plus!</h2>
            <p className="text-gray-600 mb-6">
              Your account has been upgraded. Open the Boat 360 app to access all premium features.
            </p>
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-left">
              <p className="font-semibold text-cyan-800 mb-2">You now have access to:</p>
              <ul className="text-sm text-cyan-700 space-y-1">
                <li>✓ Friends & Family Tracking</li>
                <li>✓ Private SOS Alerts</li>
                <li>✓ Background GPS Tracking</li>
                <li>✓ Float Plans</li>
                <li>✓ Trip Sharing Links</li>
                <li>✓ Overdue Alerts</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Ship className="w-8 h-8 text-cyan-600" />
          </div>
          <CardTitle className="text-2xl">Boat 360</CardTitle>
          <CardDescription>Redeem your promo code for Plus access</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRedeem} className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="redeem-email"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your account password"
                required
                data-testid="redeem-password"
              />
            </div>

            {/* Promo Code */}
            <div className="space-y-2">
              <Label htmlFor="promo" className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-purple-600" />
                Promo Code
              </Label>
              <Input
                id="promo"
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                className="uppercase font-mono text-lg tracking-wider"
                required
                data-testid="redeem-promo-code"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
              data-testid="redeem-submit"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Redeeming...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Unlock Boat 360 Plus
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-gray-500">
              Don't have an account? Download the app and sign up first.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromoRedeemPage;
